import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Video,
  User,
  Calendar,
  Clock,
  FileText,
  DollarSign,
  CheckCircle2,
  CalendarClock,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import type { AppointmentListItem, AppointmentStatus } from '@/api';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerFooter,
} from '@/components/ui/drawer';
import { AppointmentStatusBadge } from '../AppointmentStatusBadge';
import { Button } from '@/components/ui/Button';

export interface AppointmentDetailDrawerProps {
  appointment: AppointmentListItem | null;
  isOpen: boolean;
  onClose: () => void;
  onCancel: (appointmentId: string) => void;
  onReschedule: (appointment: AppointmentListItem) => void;
  onConfirm?: (appointmentId: string) => void;
  isConfirming?: boolean;
}

const CANCELLABLE_STATUSES: AppointmentStatus[] = [
  'CREATED',
  'WAITING_PAYMENT',
  'PAID',
  'WAITING_DOCTOR',
  'DOCTOR_ACCEPTED',
];

const RESCHEDULABLE_STATUSES: AppointmentStatus[] = [
  'CREATED',
  'WAITING_PAYMENT',
  'PAID',
  'WAITING_DOCTOR',
  'DOCTOR_ACCEPTED',
];

const CONFIRMABLE_STATUSES: AppointmentStatus[] = ['CREATED', 'PAID', 'WAITING_DOCTOR'];

export function AppointmentDetailDrawer({
  appointment,
  isOpen,
  onClose,
  onCancel,
  onReschedule,
  onConfirm,
  isConfirming = false,
}: AppointmentDetailDrawerProps) {
  const navigate = useNavigate();

  if (!appointment) return null;

  const scheduledDate = new Date(appointment.scheduledAt);
  const duration = appointment.duration || 30;
  const endDate = new Date(scheduledDate.getTime() + duration * 60 * 1000);

  const formattedDate = format(scheduledDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  const formattedStartTime = format(scheduledDate, 'HH:mm');
  const formattedEndTime = format(endDate, 'HH:mm');

  const formattedPrice = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(appointment.price || 0);

  const patient = appointment.patient;
  const patientName = patient?.name || 'Paciente sem nome';
  const isTelemedicine = Boolean(
    appointment.joinUrl || appointment.canJoin || appointment.type === 'EMERGENCY'
  );

  const canCancel = CANCELLABLE_STATUSES.includes(appointment.status);
  const canReschedule = RESCHEDULABLE_STATUSES.includes(appointment.status);
  const canConfirm = Boolean(onConfirm) && CONFIRMABLE_STATUSES.includes(appointment.status);
  const canJoin =
    (appointment.status === 'DOCTOR_ACCEPTED' || appointment.status === 'IN_PROGRESS') ||
    appointment.canJoin;

  const handleJoinCall = () => {
    onClose();
    if (appointment.joinUrl) {
      navigate(appointment.joinUrl);
    } else {
      navigate(`/medico/telemedicine/session/${appointment.id}`);
    }
  };

  const handleViewPatientRecord = () => {
    if (!patient?.id) return;
    onClose();
    navigate(`/medico/pacientes/${patient.id}/prontuarios`);
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-w-md w-full sm:max-w-lg">
        <DrawerHeader className="border-b border-border px-6 py-4">
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Consulta
              </span>
              <DrawerTitle className="text-lg font-bold text-foreground">
                Detalhes do Agendamento
              </DrawerTitle>
            </div>
            <div className="flex items-center gap-2">
              <AppointmentStatusBadge status={appointment.status} size="sm" />
              <DrawerClose onClose={onClose} />
            </div>
          </div>
        </DrawerHeader>

        {/* Conteúdo Principal */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Card do Paciente */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg shrink-0 overflow-hidden border border-primary/20">
                {patient?.photoUrl ? (
                  <img src={patient.photoUrl} alt={patientName} className="w-full h-full object-cover" />
                ) : (
                  <span>{patientName.charAt(0).toUpperCase()}</span>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base text-foreground truncate">{patientName}</h3>
                {patient?.email && (
                  <p className="text-xs text-muted-foreground truncate">{patient.email}</p>
                )}
                {patient?.cpf && (
                  <p className="text-xs text-muted-foreground mt-0.5">CPF: {patient.cpf}</p>
                )}

                {patient?.id && (
                  <button
                    type="button"
                    onClick={handleViewPatientRecord}
                    className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-hover transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Acessar Prontuário do Paciente</span>
                    <ExternalLink className="h-3 w-3 opacity-60" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Banner de Telemedicina com botão de acesso */}
          {canJoin && (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                <Video className="h-4 w-4" />
                <span>Sala de Telemedicina Pronta</span>
              </div>
              <p className="text-xs text-muted-foreground">
                A sessão de vídeo está disponível para atendimento em ambiente seguro e criptografado.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={handleJoinCall}
                className="w-full gap-2 font-semibold shadow-sm"
              >
                <Video className="h-4 w-4" />
                <span>Entrar na Consulta por Vídeo</span>
              </Button>
            </div>
          )}

          {/* Grade de Informações da Consulta */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Calendar className="h-3.5 w-3.5 text-primary" />
                <span>Data</span>
              </div>
              <p className="text-xs font-semibold text-foreground capitalize truncate">
                {formattedDate}
              </p>
            </div>

            <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <Clock className="h-3.5 w-3.5 text-primary" />
                <span>Horário</span>
              </div>
              <p className="text-xs font-semibold text-foreground">
                {formattedStartTime} - {formattedEndTime} ({duration}m)
              </p>
            </div>

            <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                {isTelemedicine ? (
                  <Video className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <User className="h-3.5 w-3.5 text-primary" />
                )}
                <span>Modalidade</span>
              </div>
              <p className="text-xs font-semibold text-foreground">
                {isTelemedicine ? 'Telemedicina' : 'Presencial'}
              </p>
            </div>

            <div className="rounded-lg border border-border/80 bg-muted/20 p-3">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                <DollarSign className="h-3.5 w-3.5 text-primary" />
                <span>Valor</span>
              </div>
              <p className="text-xs font-semibold text-foreground">{formattedPrice}</p>
            </div>
          </div>

          {/* Motivo ou Queixa Principal */}
          {appointment.reason && (
            <div className="rounded-lg border border-border/80 bg-card p-3.5 space-y-1">
              <span className="text-xs font-semibold text-foreground">Motivo / Queixa Principal</span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {appointment.reason}
              </p>
            </div>
          )}
        </div>

        {/* Rodapé com Ações */}
        <DrawerFooter className="border-t border-border px-6 py-4 flex-col sm:flex-row gap-2">
          {canConfirm && onConfirm && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => onConfirm(appointment.id)}
              disabled={isConfirming}
              className="w-full sm:w-auto flex-1 gap-1.5 font-medium"
            >
              <CheckCircle2 className="h-4 w-4" />
              <span>Confirmar</span>
            </Button>
          )}

          {canReschedule && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                onReschedule(appointment);
              }}
              className="w-full sm:w-auto flex-1 gap-1.5 font-medium border-border"
            >
              <CalendarClock className="h-4 w-4 text-primary" />
              <span>Reagendar</span>
            </Button>
          )}

          {canCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onClose();
                onCancel(appointment.id);
              }}
              className="w-full sm:w-auto flex-1 gap-1.5 font-medium text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            >
              <XCircle className="h-4 w-4" />
              <span>Cancelar</span>
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
